import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import translate from '../../../translate';
import Anchor from '../../../Anchor';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import ServicesContext from '../../../../contextProviders/ServicesContext';
import MetaContext from '../../../../contextProviders/MetaContext';
import UserPortalMenusProp, {
  MENU_TYPES,
} from '../../../propTypes/userPortalMenusProp';
import User from '../../../../../../../domain/models/user';
import useIsDashboardEnabled from '../../../hooks/useIsDashboardEnabled';
import MarketInfosContext from '../../../../contextProviders/MarketInfosContext';
import { useDimensions } from '../../../../services/customHooks';

function TopMenu({ userPortalMenus }) {
  const services = useContext(ServicesContext);
  const tenantConfigs = useContext(MetaContext);
  const { loggedUser } = useContext(LoggedUserContext);
  const isDashboardEnabled = useIsDashboardEnabled();
  const isLogged = !!loggedUser;
  const isCollaborator =
    loggedUser?.type === User.TYPE.COLLABORATOR ||
    loggedUser?.role === User.ROLE.ADMIN;
  const academyIsEnabled =
    services && services.academy && services.academy.enabled === true;
  const marketPlaceIsEnabled =
    services && services.marketPlace && services.marketPlace.enabled === true;
  const { isMobile } = useDimensions();

  const classes = cx(
    'm-header-menu',
    'm-aside-header-menu-mobile',
    'm-aside-header-menu-mobile--offcanvas',
    'm-header-menu--skin-light',
    'm-header-menu--submenu-skin-light',
    'm-aside-header-menu-mobile--skin-light',
    'm-aside-header-menu-mobile--submenu-skin-light',
    'academy-header-menu',
    'bravon-header-menu',
  );

  const canSeeMenu = userTypesArray => {
    // in case userTypes isn't defined
    if (!userTypesArray) {
      return true;
    }

    // in case userTypes is empty
    if (userTypesArray.length === 0) {
      return true;
    }

    if (userTypesArray.length >= 1) {
      // not empty and not logged
      if (!isLogged) {
        return false;
      }
      // not empty and is collaborator
      if (userTypesArray.includes(User.TYPE.COLLABORATOR)) {
        if (isCollaborator) {
          return true;
        }
      }
    }

    return false;
  };

  const { marketInfos } = useContext(MarketInfosContext);
  const menuConfig = useMemo(() => {
    const menus = [];
    const currentLang = marketInfos?.localLanguage;

    const getMenuObj = menu => {
      let parsedMenu = null;
      const { type, url, translations, newTab, userTypes, subMenus } = menu;
      const name = translations[currentLang];
      const hasSubMenus =
        type === MENU_TYPES.DYNAMIC && subMenus && subMenus.length > 0;

      // A dynamic parent that has sub-menus is NOT gated by its own
      // "only distributors" config — only its sub-menus carry that setting.
      // It is shown as long as at least one sub-menu is visible to the user.
      // Every other (leaf) item is gated by its own userTypes.
      if (!hasSubMenus && !canSeeMenu(userTypes)) {
        return null;
      }

      switch (type) {
        case MENU_TYPES.DASHBOARD:
          if (isDashboardEnabled) {
            parsedMenu = {
              target: newTab ? '_blank' : '_self',
              title: name,
              url: '/dashboard',
            };
          }
          break;
        case MENU_TYPES.ACADEMY:
          if (
            academyIsEnabled &&
            (isLogged || tenantConfigs.publicModules.academy)
          ) {
            parsedMenu = {
              target: newTab ? '_blank' : '_self',
              title: name,
              url: '/academy',
            };
          }
          break;
        case MENU_TYPES.MARKETPLACE:
          if (
            marketPlaceIsEnabled &&
            (isLogged || tenantConfigs.publicModules.marketPlace)
          ) {
            parsedMenu = {
              target: newTab ? '_blank' : '_self',
              title: name,
              url: '/shop',
            };
          }
          break;
        case MENU_TYPES.DYNAMIC: {
          const items = hasSubMenus
            ? subMenus.map(getMenuObj).filter(Boolean)
            : [];
          // Hide the parent dropdown when none of its sub-menus are visible
          // (e.g. all sub-menus are "only distributors" and the user is not one).
          if (hasSubMenus && items.length === 0) {
            break;
          }
          parsedMenu = {
            items,
            title: name,
            url: url?.startsWith('https://') ? url || '' : `https://${url}`,
            target: newTab ? '_blank' : '_self',
            redirectToAnotherDomain: true,
          };
          break;
        }
        default:
          console.log('*TYPE', type);
          break;
      }
      return parsedMenu;
    };

    userPortalMenus.forEach(menu => {
      const parsedMenu = getMenuObj(menu);
      if (parsedMenu) {
        menus.push(parsedMenu);
      }
    });
    return menus;
  }, [userPortalMenus]);

  useEffect(() => {
    if (isMobile) {
      // Fallback for manual initialization
      const menuItems = document.querySelectorAll(
        '[m-menu-submenu-toggle="click"]',
      );
      menuItems.forEach(item => {
        item.addEventListener('click', function (event) {
          // Only toggle if clicking on the toggle element itself, not its children
          if (
            event.currentTarget === event.target ||
            event.target.closest('.m-menu__toggle')
          ) {
            this.classList.toggle('m-menu__item--open');
          }
        });
      });
    } else {
      // Fallback for manual initialization
      const menuItems = document.querySelectorAll(
        '[m-menu-submenu-toggle="click"]',
      );
      menuItems.forEach(item => {
        item.addEventListener('click', function () {
          this.classList.toggle('m-menu__item--open');
        });
      });
    }
  }, [isMobile]);

  const renderSimpleLink = item => (
    <li key={item.url} className="m-menu__item" m-menu-link-redirect="1">
      <Anchor
        href={item.url}
        method={item.method}
        className="m-menu__link"
        target={item?.target}
        redirectToAnotherDomain={item?.redirectToAnotherDomain || false}
      >
        <span className="m-menu__link-text">{item?.title}</span>
      </Anchor>
    </li>
  );

  const renderSubMenu = item => {
    const itemClass = cx(
      'm-menu__item',
      'm-menu__item--submenu',
      'm-menu__item--rel',
    );
    if (!item.items?.find(i => i)) {
      return null;
    }
    return (
      <li
        className={itemClass}
        m-menu-submenu-toggle="click"
        m-menu-link-redirect="1"
        aria-haspopup="true"
        key={item?.url}
      >
        <div className="m-menu__link m-menu__toggle">
          <span className="m-menu__item-here" />
          <span className="m-menu__link-text">{item?.title}</span>
          <i className="m-menu__hor-arrow la la-angle-down" />
          <i className="m-menu__ver-arrow la la-angle-right" />
        </div>
        <div
          className="m-menu__submenu  m-menu__submenu--fixed m-menu__submenu--left"
          style={{ width: '250px' }}
        >
          <span className="m-menu__arrow m-menu__arrow--adjust" />
          <div className="m-menu__subnav">
            <ul className="m-menu__content">
              <li className="m-menu__item">
                {!isMobile ? (
                  <h3 className="m-menu__heading m-menu__toggle">
                    <span className="m-menu__link-text">{item?.title}</span>
                    <i className="m-menu__ver-arrow la la-angle-right" />
                  </h3>
                ) : null}
                <ul className={isMobile ? '' : 'm-menu__inner'}>
                  {item.items.map(subItem => {
                    if (!subItem) {
                      return null;
                    }
                    return (
                      <li
                        key={`sub-${subItem?.url}`}
                        className="m-menu__item "
                        m-menu-link-redirect="1"
                        aria-haspopup="true"
                      >
                        <Anchor
                          href={subItem?.url}
                          target={subItem?.target}
                          className="m-menu__link "
                          redirectToAnotherDomain={
                            subItem?.redirectToAnotherDomain || false
                          }
                        >
                          <i className="m-menu__link-bullet m-menu__link-bullet--line">
                            <span />
                          </i>
                          <span className="m-menu__link-text">
                            {subItem?.title}
                          </span>
                        </Anchor>
                      </li>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div id="m_header_menu" className={classes}>
      <ul className="m-menu__nav  m-menu__nav--submenu-arrow ">
        {menuConfig.map(item =>
          item.items?.length > 0 ? renderSubMenu(item) : renderSimpleLink(item),
        )}
      </ul>
    </div>
  );
}

TopMenu.propTypes = {
  userPortalMenus: PropTypes.arrayOf(
    PropTypes.shape(UserPortalMenusProp.propType),
  ),
};

TopMenu.defaultProps = {
  userPortalMenus: [],
};

export default translate(TopMenu);
